import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { type IModel, type ITridentResource, FabricSqlLinkedService } from 'shell-models-lib';
import { SqlServerLinkedServiceAdapter } from './sql-server-linkedservice.adapter';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(FabricSqlLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                workspaceId: '*',
                artifactId: '*',
                tdsEndpoint: '*',
                endpoint: GlobalVariables.fabricSqlED ? '*' : undefined,
                database: GlobalVariables.fabricSqlED ? '*' : undefined
            },
            parameters: '*'
        }
    }
})
export class FabricSqlLinkedServiceAdapter extends SqlServerLinkedServiceAdapter {
    protected supportFirstClassParameterExperience = true;

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Pick<Model.FabricSqlLinkedServiceTypeProperties, 'workspaceId' | 'artifactId' | 'tdsEndpoint' | 'endpoint' | 'database'> => {
        let tdsEndpoint: string;
        let endpoint: string;
        let database: string; 
        try {
            tdsEndpoint = JSON.parse(tridentResource.extendedProperties?.WarehouseProperties as string)?.tdsEndpoint;
            if (GlobalVariables.fabricSqlED) {
                const dnsConnectionString = tridentResource.extendedProperties?.DnsConnectionString as string;
                const regex = /Data Source=([^;]+)/;
                const match = dnsConnectionString?.match(regex);
                if (match) {
                    endpoint = match[1];
                }

                database = tridentResource.extendedProperties?.SourceDatabaseName as string;
            }
        } catch (e) {}
        return {
            workspaceId: tridentResource.workspaceObjectId,
            artifactId: tridentResource.objectId,
            tdsEndpoint,
            endpoint,
            database
        };
};

    protected modelConstructor(): new () => FabricSqlLinkedService {
        return FabricSqlLinkedService;
    }

    protected model(): IModel {
        return FabricSqlLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: FabricSqlLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.FabricSqlLinkedService;
        properties.type = Metamodel.modelFor(FabricSqlLinkedService).name;

        properties.typeProperties = {
            workspaceId: this.valueToResource(linkedService, FabricSqlLinkedService.WORKSPACE_ID),
            artifactId: this.valueToResource(linkedService, FabricSqlLinkedService.ARTIFACT_ID),
            endpoint: GlobalVariables.fabricSqlED ? this.valueToResource(linkedService, FabricSqlLinkedService.ENDPOINT) : undefined,
            database: GlobalVariables.fabricSqlED ? this.valueToResource(linkedService, FabricSqlLinkedService.DATABASE) : undefined
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: FabricSqlLinkedService
    ): Promise<FabricSqlLinkedService> {
        const properties = linkedServiceResource.properties as Model.FabricSqlLinkedService;
        const typeProperties: Model.FabricSqlLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, FabricSqlLinkedService.WORKSPACE_ID, typeProperties.workspaceId);
        ValueAdapter.toModel(linkedService, FabricSqlLinkedService.ARTIFACT_ID, typeProperties.artifactId);
        linkedService.sqlAnalyticsEndpoint = typeProperties.tdsEndpoint;

        if (GlobalVariables.fabricSqlED) {
            ValueAdapter.toModel(linkedService, FabricSqlLinkedService.ENDPOINT, typeProperties.endpoint);
            ValueAdapter.toModel(linkedService, FabricSqlLinkedService.DATABASE, typeProperties.database);
        }

        return linkedService;
    }
}
